import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { cognitoAuth } from '../auth/cognito';

const API_URL = import.meta.env.VITE_API_URL || 'https://roniapi.data-corner.com.au';

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 300000, // 5 minutes - allow multi-agent chat to process fully
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const token = await cognitoAuth.getIdToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    // Only log actual API errors, not cancelled/timeout/504 requests
    // 504 is expected for long-running chat requests (API Gateway 29s limit) - handled by polling
    if (error.response && error.response.status !== 504) {
      console.error('API Error:', error.response.status, error.response.data);
    } else if (!error.response && error.code !== 'ERR_CANCELED') {
      console.error('Network Error:', error.message);
    }

    if (error.response?.status === 401) {
      // Don't handle 401 for CRM routes - they have their own auth
      const isCrmRoute = window.location.pathname.startsWith('/crm');
      if (isCrmRoute) {
        return Promise.reject(error);
      }

      // Prevent infinite retry loop - if already retried, just reject
      const config = error.config as any;
      if (config?._retried) {
        return Promise.reject(error);
      }

      // Try to refresh token for admin routes
      const refreshed = await cognitoAuth.refreshSession();
      if (refreshed && error.config) {
        // Mark as retried to prevent loop
        (error.config as any)._retried = true;
        // Retry the original request
        const token = await cognitoAuth.getIdToken();
        if (token) {
          error.config.headers.Authorization = `Bearer ${token}`;
          return apiClient.request(error.config);
        }
      }
      // If refresh failed, redirect to admin login
      window.location.href = '/';
    }

    if (error.response?.status === 403) {
      console.error('Access forbidden - check if user has super_admin role');
    }

    return Promise.reject(error);
  }
);

export { apiClient };
export default apiClient;
